import React, { useCallback, useEffect, useMemo } from 'react';

// Material
import { withStyles } from '@mui/styles';

// Project
import { composedComponent } from 'utils/functions';
import { Typography } from '@mui/material';
import useStep from 'utils/hooks/useStep';
import {
  formatValidity,
  getMarginPerUnit,
  getPrice,
  getQuoteExpiryDate,
  getTotalMargin,
  getTotalSale,
} from 'utils/quoteCalculations';
import { sumByKey } from 'utils/math';
import { getDollarKardex, getDollarZcot } from 'utils/currencyHelpers';
// Hooks
import useCurrency from 'utils/hooks/useCurrency';
// Saga
import { quoteActions } from './reducer';
import saga from './saga';

// Components
import Form from './components/Form';
import QuoteStepper from './components/QuoteStepper';
import Preview from './components/Preview';
import FinalStep from './components/FinalStep';
import ModalSelectCost from './components/ModalSelectCost';

// Utils

// Styles
import styles from './styles';

const Quote = function (props) {
  const {
    user,
    objects,
    classes,
    controls,
    actions,
    step,
    linesHasChanged,
    newQuoteId,
    costChoice,
    clientLookup,
  } = props;

  const dollarKardex = getDollarKardex(objects.currencies);
  const dollarZcot = getDollarZcot(objects.currencies);
  const { currencyCode, setCurrencyCode, convertKardexValue, convertZcotValue } = useCurrency({
    dollarKardex,
    dollarZcot,
  });

  useEffect(() => {
    switch (currencyCode) {
      case 'USD':
        actions.changeControls2('currencyId', '1');
        break;
      case 'CLP':
        actions.changeControls2('currencyId', '4');
        break;
      default:
        actions.changeControls2('currencyId', '1');
        break;
    }
  }, [currencyCode, actions]);

  // Los datos del ejecutivo parten con los del usuario conectado y quedan editables, por si
  // cotiza a nombre de otro representante.
  const fillExecutiveData = useCallback(() => {
    actions.changeControls({
      sender: user.user.name || '',
      email: user.user.email || '',
      phone: user.user.phone || '',
    });
  }, [actions, user.user]);

  // Solo si están vacíos: al volver de otra pantalla no se pisan los datos que el usuario
  // haya cambiado a mano.
  useEffect(() => {
    if (!controls.sender && !controls.email && !controls.phone) fillExecutiveData();
  }, []);

  // La cotización se mantiene en memoria al ir a Fórmula y volver. Si mientras tanto se guardó
  // un ZCOR nuevo para un material ya agregado, la línea quedaría con el costo anterior, así
  // que al volver a la pantalla (o a la pestaña del navegador) se revisa y se vuelve a preguntar.
  const materialIdsKey = controls.materialLines.map((line) => line.m_code).join(',');
  const checkZcorVersions = useCallback(() => {
    if (step !== 0 || !materialIdsKey) return;
    actions.checkZcorVersions({ materialIds: materialIdsKey.split(',').map(Number) });
  }, [step, materialIdsKey, actions]);

  // También al volver al resumen desde la previsualización (p. ej. después de que el backend
  // rechazó emitir porque una versión cambió).
  useEffect(() => {
    checkZcorVersions();
  }, [step]);

  useEffect(() => {
    window.addEventListener('focus', checkZcorVersions);
    return () => window.removeEventListener('focus', checkZcorVersions);
  }, [checkZcorVersions]);

  const calculatedValues = useMemo(() => {
    const calculations = controls.materialLines.map((row) => ({
      price: getPrice(row?.projectedCost, row?.mgPerc),
      marginPerUnit: getMarginPerUnit(row?.kardex, row?.projectedCost, row?.mgPerc),
      totalMargin: getTotalMargin(row?.projectedCost, row?.mgPerc, row?.volumeKg),
      totalSale: getTotalSale(row?.projectedCost, row?.mgPerc, row?.volumeKg),
    }));
    const totals = {
      // price: sumByKey(calculations, 'price'),
      totalMargin: sumByKey(calculations, 'totalMargin'),
      totalSale: sumByKey(calculations, 'totalSale'),
    };
    return { calculations, totals };
  }, [controls.materialLines]);

  const changeStep = (value) => {
    actions.changeState({ step: value });
  };

  const resetForm = useCallback(() => {
    actions.reset();
    fillExecutiveData();
  }, [actions, fillExecutiveData]);

  const [setPrevStep, setNextStep] = useStep(step, { steps: 3 }, changeStep);

  const handleRemoveMaterial = (index) => {
    const lines = controls.materialLines;
    const newLines = lines.filter((line, idx) => idx !== index);
    actions.changeControls({ materialLines: newLines });
    actions.changeState({ linesHasChanged: true });
  };

  const handleChange = useCallback(
    (key, value) => {
      actions.changeControls2(key, value);
    },
    [actions],
  );

  const processedQuoteData = useMemo(() => {
    const mergedLines = controls.materialLines.map((row, idx) => {
      const { price, totalMargin, totalSale } = calculatedValues.calculations[idx];
      return {
        ...row,
        // Costo en USD de la versión ZCOR usada, sin convertir: el backend lo compara con la
        // versión al emitir, por si alguien la editó mientras se armaba la cotización.
        formulaVersionCost: row?.formulaVersionId ? row.projectedCost : null,
        kardex: convertKardexValue(row?.kardex),
        projectedCost: convertZcotValue(row?.projectedCost),
        zcotProjectedCost: convertZcotValue(row?.zcotProjectedCost),
        zcot: convertZcotValue(row?.zcot),
        calculatedPrice: convertKardexValue(row?.calculatedPrice),
        // price: convertKardexValue(price), // this values is the worng one, but before it was being used
        price: convertZcotValue(price),
        // Mismo dólar que el precio y que lo que se muestra en pantalla.
        totalMargin: convertZcotValue(totalMargin),
        totalSale: convertZcotValue(totalSale),
      };
    });
    const result = {
      ...controls,
      endDate: getQuoteExpiryDate(controls.startDate, controls.validityDays),
      validity: formatValidity(controls.validityDays),
      materialLines: mergedLines,
    };
    return result;
  }, [controls, calculatedValues]);

  const handleSubmitQuote = useCallback(() => {
    const newData = processedQuoteData;
    actions.submitQuote(newData);
  }, [processedQuoteData, actions]);

  return (
    <>
      <Typography variant="h3" mb="30px">
        Resumen
      </Typography>
      <QuoteStepper step={step} />
      {step === 0 && (
        <Form
          linesHasChanged={linesHasChanged}
          controls={controls}
          actions={actions}
          setNextStep={setNextStep}
          classes={classes}
          calculatedValues={calculatedValues}
          handleChange={handleChange}
          handleRemoveMaterial={handleRemoveMaterial}
          currencyCode={currencyCode}
          setCurrencyCode={setCurrencyCode}
          convertKardexValue={convertKardexValue}
          convertZcotValue={convertZcotValue}
          clientLookup={clientLookup}
        />
      )}
      {step === 1 && (
        <Preview
          setPrevStep={setPrevStep}
          setNextStep={setNextStep}
          actions={actions}
          calculatedValues={calculatedValues}
          data={processedQuoteData}
          handleSubmitQuote={handleSubmitQuote}
        />
      )}
      {step === 2 && (
        <FinalStep
          setPrevStep={setPrevStep}
          resetForm={resetForm}
          actions={actions}
          newQuoteId={newQuoteId}
        />
      )}
      <ModalSelectCost
        open={costChoice.open}
        costChoice={costChoice}
        actions={actions}
        convertZcotValue={convertZcotValue}
        currencyCode={currencyCode}
      />
    </>
  );
};

export default composedComponent(Quote, saga, {
  saga: 'sagaQuote',
  middlewares: [withStyles(styles)],
  states: [
    'app.user',
    'app.objects',
    'quote.controls',
    'quote.invalidControls',
    'quote.step',
    'quote.linesHasChanged',
    'quote.newQuoteId',
    'quote.costChoice',
    'quote.clientLookup',
  ],
  actions: [quoteActions],
});
